import React from 'react'
import { Link } from 'gatsby'
import styled from 'styled-components'
import { withStyles } from '@material-ui/core/styles'
import {
  ButtonBase,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core'
import { AccountCircle, Close, KeyboardReturn } from '@material-ui/icons'
import ContextConsumer from './Context'
import AccountMenuList from './AccountMenuList'
import LanguageSelect from './LanguageSelect'
import { removeUser } from '../utils/localStorage'
import { formatPrice } from '../utils/helpers'

const AccountDrawer = styled(Drawer)`
  width: 300px;
  padding: ${props => props.theme.spacing(6)}px;
`
const DrawerIconButton = styled(IconButton)`
  margin-top: -12px !important;
  margin-bottom: 6px;
  margin-right: -8px !important;
  height: 52px;
`
const DrawerHeader = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  height: 64px;
  margin-left: ${props => props.theme.spacing(4)}px;
  margin-bottom: ${props => props.theme.spacing(2)}px;
  padding: ${props => props.theme.spacing(4)}px
    ${props => props.theme.spacing(4)}px ${props => props.theme.spacing(0)}px
    ${props => props.theme.spacing(4)}px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
`
const DrawerFooter = styled.div`
  position: absolute;
  bottom: ${props => props.theme.spacing(4)}px;
  left: 0;
  right: 0;
`
const DrawerTitle = styled.div`
  font-size: 1rem;
`
const AccountHeader = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  margin-left: ${props => props.theme.spacing(4)}px;
  margin-bottom: ${props => props.theme.spacing(2)}px;
  padding: ${props => props.theme.spacing(4)}px
    ${props => props.theme.spacing(8)}px ${props => props.theme.spacing(4)}px
    ${props => props.theme.spacing(4)}px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
`
const AccountProfile = styled.div`
  display: flex;
  margin-bottom: ${props => props.theme.spacing(2)}px;
`
const AccountCircleIcon = styled(AccountCircle)`
  width: 52px !important;
  height: 52px !important;
  margin-right: ${props => props.theme.spacing(2)}px;
  color: ${props => props.theme.palette.primary.main};
`
const AccountDetails = styled.div`
  padding: ${props => props.theme.spacing(1)}px;
`
const AccountName = styled.div`
  line-height: 1.5rem;
`
const AccountBalance = styled.div`
  font-size: 0.778rem;
  line-height: 1.25rem;
  color: ${props => props.theme.palette.primary.main};
`
const BonusBet = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${props => props.theme.spacing(1)}px;
  margin-bottom: ${props => props.theme.spacing(1)}px;
`
const BonusLabel = styled.div`
  font-size: 0.778rem;
  color: ${props => props.theme.palette.text.secondary};
`
const BonusBalance = styled.div`
  font-size: 0.778rem;
  font-weight: 600;
  color: ${props => props.theme.palette.primary.main};
`
const AccountButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${props => props.theme.spacing(2)}px;
  margin-bottom: ${props => props.theme.spacing(2)}px;
`
const SolidButton = styled.div`
  padding: ${props => props.theme.spacing(2)}px
    ${props => props.theme.spacing(3)}px;
  background-color: ${props => props.theme.palette.primary.main};
  border: 1px solid ${props => props.theme.palette.primary.main};
  color: #fff;
  font-size: 0.778rem;
  font-weight: 600;
  text-transform: uppercase;
`
const OutlineButton = styled.div`
  padding: ${props => props.theme.spacing(2)}px
    ${props => props.theme.spacing(3)}px;
  background-color: transparent;
  border: 1px solid ${props => props.theme.palette.primary.main};
  color: ${props => props.theme.palette.primary.main};
  font-size: 0.778rem;
  font-weight: 600;
  text-transform: uppercase;
`
const SignOutButton = styled.div`
  display: flex;
  margin-right: ${props => props.theme.spacing(2)}px;
  margin-left: ${props => props.theme.spacing(2)}px;
  padding: ${props => props.theme.spacing(2)}px
    ${props => props.theme.spacing(3)}px;
  color: ${props => props.theme.palette.text.secondary};
  font-size: 0.778rem;
  text-transform: uppercase;
`
const MenuListItemText = styled(ListItemText)`
  padding-left: 0;
`

const AccountMenu = () => (
  <ContextConsumer>
    {({ data, set }) => {
      const hasSelectedUser = Object.keys(data.user).length > 0
      const handleSignOut = () => {
        set({ user: {} })
        removeUser()
      }
      return (
        <AccountDrawer
          anchor="right"
          open={data.accountMenuOpen}
          onClose={() => set({ accountMenuOpen: !data.accountMenuOpen })}
        >
          {hasSelectedUser ? (
            <>
              <DrawerHeader>
                <DrawerTitle>My Profile</DrawerTitle>
                <DrawerIconButton
                  onClick={() =>
                    set({ accountMenuOpen: !data.accountMenuOpen })
                  }
                >
                  <Close />
                </DrawerIconButton>
              </DrawerHeader>
              <AccountHeader>
                <AccountProfile>
                  <AccountCircleIcon />
                  <AccountDetails>
                    <AccountName>{data.user.name}</AccountName>
                    <AccountBalance>
                      {formatPrice(data.user.balance * 100)}
                    </AccountBalance>
                  </AccountDetails>
                </AccountProfile>
                {data.user.freebet && (
                  <BonusBet>
                    <BonusLabel>Bonus:</BonusLabel>
                    <BonusBalance>Has free bet</BonusBalance>
                  </BonusBet>
                )}
                <AccountButtons>
                  <ButtonBase>
                    <SolidButton>Deposit</SolidButton>
                  </ButtonBase>
                  <ButtonBase>
                    <OutlineButton>Withdraw</OutlineButton>
                  </ButtonBase>
                </AccountButtons>
              </AccountHeader>
              <AccountMenuList />
              <DrawerFooter>
                <List
                  component="nav"
                  style={{ marginRight: '16px', marginLeft: '16px' }}
                >
                  <ListItem button onClick={handleSignOut}>
                    <ListItemIcon>
                      <KeyboardReturn />
                    </ListItemIcon>
                    <MenuListItemText inset primary="Sign Out" />
                  </ListItem>
                </List>
              </DrawerFooter>
            </>
          ) : (
            <>
              <DrawerHeader>
                <DrawerTitle>Menu Options</DrawerTitle>
                <DrawerIconButton
                  onClick={() =>
                    set({ accountMenuOpen: !data.accountMenuOpen })
                  }
                >
                  <Close />
                </DrawerIconButton>
              </DrawerHeader>
              <AccountMenuList />
              <DrawerFooter>
                <LanguageSelect />
              </DrawerFooter>
            </>
          )}
        </AccountDrawer>
      )
    }}
  </ContextConsumer>
)

export default AccountMenu
